import { alternateVideoRepoPaths, videoRepoPath } from './itemVideo';
import { logThumbDebug } from './mediaDebugLog';
import { isFirefoxBrowser, prefersVideoStreamPlayback } from './videoPlaybackMode';

export type VideoThumbnailCaptureOptions = {
  /** Optional mount for tests; mobile uses hidden host automatically. */
  visibleMount?: HTMLElement;
};

/** `clip.mp4` → `clip.thumb.jpg` (JPEG previews for gallery tiles). */
export function videoThumbnailFilename(videoFilename: string): string {
  const dot = videoFilename.lastIndexOf('.');
  const base = dot > 0 ? videoFilename.slice(0, dot) : videoFilename;
  return `${base}.thumb.jpg`;
}

export function videoThumbnailRepoPath(mdPath: string, videoFilename: string): string {
  return videoRepoPath(mdPath, videoThumbnailFilename(videoFilename));
}

export function videoThumbnailRepoPathFromFull(videoRepoPathStr: string): string {
  const slash = videoRepoPathStr.lastIndexOf('/');
  if (slash < 0) return videoThumbnailFilename(videoRepoPathStr);
  return `${videoRepoPathStr.slice(0, slash + 1)}${videoThumbnailFilename(videoRepoPathStr.slice(slash + 1))}`;
}

/** Repo paths to try when loading a committed video thumbnail. */
export function videoThumbLoadRepoPaths(videoRepoPath: string, mdPath?: string): string[] {
  const bases = alternateVideoRepoPaths(videoRepoPath, mdPath);
  return [...new Set(bases.map(videoThumbnailRepoPathFromFull))];
}

/** Frames darker than this average luminance are treated as failed captures. */
export const BLACK_THUMB_LUMINANCE_MAX = 14;

const SEEK_TIMES = [0.12, 0.5, 1, 2] as const;
const THUMB_SAMPLE_SIZE = 32;
const OFFSCREEN_MAX_EDGE = 480;
const QUICK_CAPTURE_TIMEOUT_MS = 8_000;
const QUICK_PLAY_PULSE_MS = 60;
const TILE_PLAY_PULSE_MS = 400;

export function isAcceptableThumbLuminance(lum: number | null): boolean {
  return lum !== null && lum >= BLACK_THUMB_LUMINANCE_MAX;
}

function needsQuickHiddenCapture(): boolean {
  return prefersVideoStreamPlayback() || isFirefoxBrowser();
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function stopCaptureVideo(video: HTMLVideoElement): void {
  video.pause();
  video.loop = false;
  video.removeAttribute('src');
  video.load();
}

let activeCaptureSession = 0;

function endAllThumbCaptureSessions(): void {
  for (const overlay of document.querySelectorAll('.video-thumb-capture-overlay')) {
    overlay.remove();
  }
  for (const host of document.querySelectorAll('.video-thumb-capture-hidden-host')) {
    for (const v of host.querySelectorAll('video')) stopCaptureVideo(v);
    host.remove();
  }
}

function beginThumbCaptureSession(): { id: number; overlay: HTMLElement; videoHost: HTMLElement } {
  endAllThumbCaptureSessions();
  const id = ++activeCaptureSession;

  const overlay = document.createElement('div');
  overlay.className = 'video-thumb-capture-overlay';
  overlay.dataset['thumbCaptureSession'] = String(id);
  const label = document.createElement('p');
  label.className = 'video-thumb-capture-label';
  label.textContent = 'Creating preview…';
  overlay.appendChild(label);
  document.body.appendChild(overlay);

  const videoHost = document.createElement('div');
  videoHost.className = 'video-thumb-capture-hidden-host';
  videoHost.dataset['thumbCaptureSession'] = String(id);
  document.body.appendChild(videoHost);

  return { id, overlay, videoHost };
}

function endThumbCaptureSession(session: { id: number; overlay: HTMLElement; videoHost: HTMLElement }): void {
  if (session.id !== activeCaptureSession) return;
  for (const v of session.videoHost.querySelectorAll('video')) stopCaptureVideo(v);
  session.videoHost.remove();
  session.overlay.remove();
}

function attachOffscreen(video: HTMLVideoElement): () => void {
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-9999px;top:0;width:${OFFSCREEN_MAX_EDGE}px;height:${OFFSCREEN_MAX_EDGE}px;opacity:0.01;pointer-events:none;overflow:hidden`;
  video.style.maxWidth = `${OFFSCREEN_MAX_EDGE}px`;
  video.style.maxHeight = `${OFFSCREEN_MAX_EDGE}px`;
  video.style.width = '100%';
  video.style.height = 'auto';
  host.appendChild(video);
  document.body.appendChild(host);
  return () => host.remove();
}

function uniqueSeekTargets(duration: number): number[] {
  const targets = SEEK_TIMES.map(seek =>
    duration > 0 ? Math.min(seek, Math.max(0, duration - 0.05)) : seek
  );
  return [...new Set(targets.map(t => Math.round(t * 1000) / 1000))];
}

/** Seek times for short mobile clips; 0.5s first (works on Firefox Android). */
export function quickSeekTargets(duration: number): number[] {
  if (!Number.isFinite(duration) || duration <= 0) return [0];
  const end = Math.max(0, duration - 0.05);
  const candidates = [0.5, end, 0.12, duration * 0.4].map(s => Math.min(Math.max(0, s), end));
  return [...new Set(candidates.map(t => Math.round(t * 1000) / 1000))];
}

async function waitForEvent(el: HTMLVideoElement, name: string, timeoutMs: number): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`video ${name} timeout`)), timeoutMs);
    el.addEventListener(
      name,
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
    el.addEventListener(
      'error',
      () => {
        clearTimeout(timer);
        reject(new Error(`video error during ${name}`));
      },
      { once: true }
    );
  });
}

async function waitForFrameReady(video: HTMLVideoElement, timeoutMs: number): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;
  const rvfc = (
    video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    }
  ).requestVideoFrameCallback;
  if (rvfc) {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('video frame timeout')), timeoutMs);
      rvfc.call(video, () => {
        clearTimeout(timer);
        resolve();
      });
    });
    return;
  }
  await waitForEvent(video, 'loadeddata', timeoutMs);
}

export function averageLuminanceFromRgba(data: Uint8ClampedArray): number {
  const pixels = data.length / 4;
  if (pixels === 0) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += data[i]! * 0.299 + data[i + 1]! * 0.587 + data[i + 2]! * 0.114;
  }
  return sum / pixels;
}

export function isMostlyBlackImageData(
  data: Uint8ClampedArray,
  maxLuminance = BLACK_THUMB_LUMINANCE_MAX
): boolean {
  return averageLuminanceFromRgba(data) < maxLuminance;
}

function luminanceFromImageSource(
  source: CanvasImageSource,
  width: number,
  height: number
): number | null {
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SAMPLE_SIZE;
  canvas.height = THUMB_SAMPLE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx || width <= 0 || height <= 0) return null;
  ctx.drawImage(source, 0, 0, THUMB_SAMPLE_SIZE, THUMB_SAMPLE_SIZE);
  return averageLuminanceFromRgba(ctx.getImageData(0, 0, THUMB_SAMPLE_SIZE, THUMB_SAMPLE_SIZE).data);
}

export async function isMostlyBlackImageElement(
  img: HTMLImageElement,
  maxLuminance = BLACK_THUMB_LUMINANCE_MAX
): Promise<boolean> {
  const lum = luminanceFromImageSource(img, img.naturalWidth, img.naturalHeight);
  return lum !== null && lum < maxLuminance;
}

export async function averageLuminanceFromBlob(blob: Blob): Promise<number | null> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    try {
      return luminanceFromImageSource(bitmap, bitmap.width, bitmap.height);
    } finally {
      bitmap.close();
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('thumb blob decode failed'));
      img.src = url;
    });
    return luminanceFromImageSource(img, img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function isMostlyBlackBlob(
  blob: Blob,
  maxLuminance = BLACK_THUMB_LUMINANCE_MAX
): Promise<boolean> {
  const lum = await averageLuminanceFromBlob(blob);
  return lum !== null && lum < maxLuminance;
}

export function waitForImageLoad(img: HTMLImageElement, timeoutMs = 8000): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('image load timeout')), timeoutMs);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const fail = () => {
      clearTimeout(timer);
      reject(new Error('image load error'));
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', fail, { once: true });
  });
}

async function captureFrameImageCapture(
  video: HTMLVideoElement,
  maxEdge: number
): Promise<Blob | null> {
  const captureStream = (
    video as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream }
  ).captureStream?.() ?? (
    video as HTMLVideoElement & { mozCaptureStream?: () => MediaStream }
  ).mozCaptureStream?.();
  if (!captureStream || typeof ImageCapture === 'undefined') return null;

  const track = captureStream.getVideoTracks()[0];
  if (!track) return null;

  try {
    const ic = new ImageCapture(track);
    const bitmap = await ic.grabFrame();
    try {
      const w = bitmap.width;
      const h = bitmap.height;
      if (!w || !h) return null;
      const scale = Math.min(1, maxEdge / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.82)
      );
    } finally {
      bitmap.close();
    }
  } finally {
    track.stop();
  }
}

async function waitForPaintedFrame(): Promise<void> {
  await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

async function captureFrame(video: HTMLVideoElement, maxEdge: number): Promise<Blob> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('Video has no frame dimensions');

  await waitForPaintedFrame();

  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));

  let source: CanvasImageSource = video;
  let bitmap: ImageBitmap | null = null;
  if (typeof createImageBitmap === 'function') {
    try {
      bitmap = await createImageBitmap(video);
      source = bitmap;
    } catch {
      /* draw from video element */
    }
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available');
    ctx.drawImage(source, 0, 0, outW, outH);

    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', 0.82)
    );
    if (!blob) throw new Error('Could not encode video thumbnail');
    return blob;
  } finally {
    bitmap?.close();
  }
}

function thumbErrMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function configureCaptureVideo(video: HTMLVideoElement): void {
  video.muted = true;
  video.loop = false;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.preload = 'auto';
  video.removeAttribute('crossorigin');
  video.addEventListener('ended', () => video.pause());
}

async function tryCaptureAtTime(
  video: HTMLVideoElement,
  target: number,
  maxEdge: number,
  debugRepoPath: string,
  usePlayPulse: boolean,
  captureMode: string,
  playPulseMs = QUICK_PLAY_PULSE_MS
): Promise<Blob | null> {
  video.pause();
  video.currentTime = target;
  try {
    await waitForEvent(video, 'seeked', 3000);
  } catch {
    return null;
  }

  await waitForFrameReady(video, 2000).catch(() => {});

  let blob = await captureFrame(video, maxEdge);
  let lum = await averageLuminanceFromBlob(blob);
  if (isAcceptableThumbLuminance(lum)) {
    logThumbDebug(debugRepoPath, 'thumb-seek-ok', `Captured frame at ${target}s`, true, {
      seek: target,
      bytes: blob.size,
      luminance: lum,
      method: 'seek',
      captureMode,
    });
    return blob;
  }

  if (!usePlayPulse) {
    logThumbDebug(debugRepoPath, 'thumb-seek-black', `Frame at ${target}s is mostly black`, false, {
      seek: target,
      bytes: blob.size,
      luminance: lum,
      method: 'seek',
      captureMode,
    });
    return null;
  }

  try {
    await video.play();
    await sleep(playPulseMs);
  } catch {
    /* ignore */
  } finally {
    video.pause();
  }

  await waitForFrameReady(video, 1500).catch(() => {});

  blob = await captureFrame(video, maxEdge);
  lum = await averageLuminanceFromBlob(blob);
  if (isAcceptableThumbLuminance(lum)) {
    logThumbDebug(debugRepoPath, 'thumb-seek-ok', `Captured frame at ${target}s (play pulse)`, true, {
      seek: target,
      bytes: blob.size,
      luminance: lum,
      method: 'play-pulse',
      captureMode,
    });
    return blob;
  }

  if (captureMode === 'tile-visible') {
    const icBlob = await captureFrameImageCapture(video, maxEdge);
    if (icBlob) {
      const icLum = await averageLuminanceFromBlob(icBlob);
      if (isAcceptableThumbLuminance(icLum)) {
        logThumbDebug(debugRepoPath, 'thumb-seek-ok', `ImageCapture at ${target}s`, true, {
          seek: target,
          bytes: icBlob.size,
          luminance: icLum,
          method: 'image-capture',
          captureMode,
        });
        return icBlob;
      }
    }
  }

  logThumbDebug(debugRepoPath, 'thumb-seek-black', `Frame at ${target}s is mostly black`, false, {
    seek: target,
    bytes: blob.size,
    luminance: lum,
    method: 'play-pulse',
    captureMode,
  });
  return null;
}

async function captureBrightestFrameDuringPlay(
  video: HTMLVideoElement,
  maxEdge: number,
  debugRepoPath: string,
  captureMode: string
): Promise<Blob | null> {
  video.pause();
  video.currentTime = 0;
  try {
    await waitForEvent(video, 'seeked', 2000);
  } catch {
    return null;
  }

  let bestBlob: Blob | null = null;
  let bestBytes = 0;
  let bestLum = -1;
  let samples = 0;
  const maxMs = Math.min(Math.max((video.duration || 1) * 1000 + 400, 900), 4000);
  const deadline = Date.now() + maxMs;

  const sample = async () => {
    if (Date.now() > deadline) return;
    try {
      const blob = await captureFrame(video, maxEdge);
      const lum = await averageLuminanceFromBlob(blob);
      samples += 1;
      if (lum !== null && lum > bestLum) {
        bestLum = lum;
        bestBlob = blob;
        bestBytes = blob.size;
      }
    } catch {
      /* ignore sample errors */
    }
  };

  try {
    await video.play();
    while (Date.now() < deadline && !video.ended) {
      await sample();
      await sleep(50);
    }
    await sample();
  } catch {
    return null;
  } finally {
    video.pause();
  }

  if (bestBlob !== null && isAcceptableThumbLuminance(bestLum)) {
    logThumbDebug(debugRepoPath, 'thumb-play-scan-ok', 'Brightest frame during playback', true, {
      luminance: bestLum,
      bytes: bestBytes,
      samples,
      captureMode,
    });
    return bestBlob;
  }

  logThumbDebug(debugRepoPath, 'thumb-play-scan-fail', 'No bright frame during playback', false, {
    bestLuminance: bestLum,
    samples,
    captureMode,
  });
  return null;
}

async function extractThumbnailFromMountedVideo(
  video: HTMLVideoElement,
  maxEdge: number,
  debugRepoPath: string,
  captureMode: string,
  playPulseMs: number
): Promise<Blob> {
  logThumbDebug(debugRepoPath, 'thumb-extract-start', 'Capturing from mount', true, { captureMode });

  await waitForEvent(video, 'loadedmetadata', 10_000);
  logThumbDebug(debugRepoPath, 'thumb-metadata-ok', 'Video metadata loaded', true, {
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    duration: video.duration,
    captureMode,
  });
  try {
    await waitForEvent(video, 'loadeddata', 10_000);
  } catch {
    await waitForFrameReady(video, 3000);
  }

  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  for (const target of quickSeekTargets(duration)) {
    logThumbDebug(debugRepoPath, 'thumb-seek-try', `Seek to ${target}s`, true, { seek: target, captureMode });
    const blob = await tryCaptureAtTime(
      video,
      target,
      maxEdge,
      debugRepoPath,
      true,
      captureMode,
      playPulseMs
    );
    if (blob) return blob;
  }

  logThumbDebug(debugRepoPath, 'thumb-play-scan-start', 'Scanning frames during one play-through', true, {
    captureMode,
  });
  const scanned = await captureBrightestFrameDuringPlay(video, maxEdge, debugRepoPath, captureMode);
  if (scanned) return scanned;

  throw new Error('Could not capture video frame');
}

async function extractWithTileMount(
  workingUrl: string,
  maxEdge: number,
  pathLabel: string,
  mount: HTMLElement
): Promise<Blob> {
  const video = document.createElement('video');
  video.className = 'item-video-thumb-capture-video';
  configureCaptureVideo(video);
  mount.classList.add('item-video-thumb-capturing');
  mount.insertBefore(video, mount.firstChild);
  video.src = workingUrl;

  try {
    const blob = await Promise.race([
      extractThumbnailFromMountedVideo(video, maxEdge, pathLabel, 'tile-visible', TILE_PLAY_PULSE_MS),
      new Promise<Blob>((_, reject) =>
        window.setTimeout(
          () => reject(new Error('thumbnail capture timeout')),
          QUICK_CAPTURE_TIMEOUT_MS
        )
      ),
    ]);
    logThumbDebug(pathLabel, 'thumb-extract-ok', `Thumbnail ${blob.size} bytes`, true, {
      captureMode: 'tile-visible',
    });
    return blob;
  } catch (err) {
    logThumbDebug(pathLabel, 'thumb-extract-fail', thumbErrMessage(err), false, {
      captureMode: 'tile-visible',
    });
    throw err;
  } finally {
    stopCaptureVideo(video);
    video.remove();
    mount.classList.remove('item-video-thumb-capturing');
  }
}

/** Firefox/mobile: text overlay only; video decodes in a hidden host (no visible playback). */
async function extractThumbnailQuickHidden(
  workingUrl: string,
  maxEdge: number,
  debugRepoPath: string,
  videoHost: HTMLElement
): Promise<Blob> {
  const video = document.createElement('video');
  video.className = 'video-thumb-capture-hidden-video';
  configureCaptureVideo(video);
  videoHost.appendChild(video);
  video.src = workingUrl;

  logThumbDebug(debugRepoPath, 'thumb-extract-start', 'Quick hidden capture', true, {
    captureMode: 'quick-hidden',
  });

  try {
    await waitForEvent(video, 'loadedmetadata', 10_000);
    logThumbDebug(debugRepoPath, 'thumb-metadata-ok', 'Video metadata loaded', true, {
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      duration: video.duration,
      captureMode: 'quick-hidden',
    });
    try {
      await waitForEvent(video, 'loadeddata', 10_000);
    } catch {
      await waitForFrameReady(video, 3000);
    }

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    for (const target of quickSeekTargets(duration)) {
      logThumbDebug(debugRepoPath, 'thumb-seek-try', `Seek to ${target}s`, true, {
        seek: target,
        captureMode: 'quick-hidden',
      });
      const blob = await tryCaptureAtTime(
        video,
        target,
        maxEdge,
        debugRepoPath,
        true,
        'quick-hidden',
        QUICK_PLAY_PULSE_MS
      );
      if (blob) return blob;
    }

    throw new Error('Could not capture video frame');
  } finally {
    stopCaptureVideo(video);
    video.remove();
  }
}

async function extractWithQuickHiddenSession(
  workingUrl: string,
  maxEdge: number,
  pathLabel: string
): Promise<Blob> {
  const session = beginThumbCaptureSession();
  try {
    const blob = await Promise.race([
      extractThumbnailQuickHidden(workingUrl, maxEdge, pathLabel, session.videoHost),
      new Promise<Blob>((_, reject) =>
        window.setTimeout(
          () => reject(new Error('thumbnail capture timeout')),
          QUICK_CAPTURE_TIMEOUT_MS
        )
      ),
    ]);
    logThumbDebug(pathLabel, 'thumb-extract-ok', `Thumbnail ${blob.size} bytes`, true, {
      captureMode: 'quick-hidden',
    });
    return blob;
  } catch (err) {
    logThumbDebug(pathLabel, 'thumb-extract-fail', thumbErrMessage(err), false, {
      captureMode: 'quick-hidden',
    });
    throw err;
  } finally {
    endThumbCaptureSession(session);
    endAllThumbCaptureSessions();
  }
}

async function extractThumbnailOffscreen(
  video: HTMLVideoElement,
  maxEdge: number,
  debugRepoPath: string
): Promise<Blob> {
  configureCaptureVideo(video);

  logThumbDebug(debugRepoPath, 'thumb-extract-start', 'Offscreen capture', true, {
    captureMode: 'offscreen',
  });

  await waitForEvent(video, 'loadedmetadata', 12_000);
  try {
    await waitForEvent(video, 'loadeddata', 12_000);
  } catch {
    await waitForFrameReady(video, 4000);
  }

  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  let lastErr: unknown;

  for (const target of uniqueSeekTargets(duration)) {
    logThumbDebug(debugRepoPath, 'thumb-seek-try', `Seek to ${target}s`, true, {
      seek: target,
      captureMode: 'offscreen',
    });
    try {
      const blob = await tryCaptureAtTime(
        video,
        target,
        maxEdge,
        debugRepoPath,
        false,
        'offscreen'
      );
      if (blob) return blob;
      lastErr = new Error('Captured frame is mostly black');
    } catch (err) {
      lastErr = err;
      logThumbDebug(debugRepoPath, 'thumb-seek-fail', thumbErrMessage(err), false, {
        seek: target,
        captureMode: 'offscreen',
      });
    }
  }

  throw lastErr instanceof Error ? lastErr : new Error('Could not capture video frame');
}

async function extractWithOffscreen(workingUrl: string, maxEdge: number, pathLabel: string): Promise<Blob> {
  const video = document.createElement('video');
  video.src = workingUrl;
  const detach = attachOffscreen(video);
  try {
    const blob = await extractThumbnailOffscreen(video, maxEdge, pathLabel);
    logThumbDebug(pathLabel, 'thumb-extract-ok', `Thumbnail ${blob.size} bytes`, true, {
      captureMode: 'offscreen',
    });
    return blob;
  } catch (err) {
    logThumbDebug(pathLabel, 'thumb-extract-fail', thumbErrMessage(err), false, {
      captureMode: 'offscreen',
    });
    throw err;
  } finally {
    detach();
  }
}

/** Extract a JPEG frame from a video URL (local blob URL only; not for repo playback). */
export async function createVideoThumbnailFromUrl(
  url: string,
  maxEdge = 400,
  debugRepoPath?: string,
  opts?: VideoThumbnailCaptureOptions
): Promise<Blob> {
  const pathLabel = debugRepoPath ?? url.slice(0, 120);
  if (opts?.visibleMount) {
    return extractWithTileMount(url, maxEdge, pathLabel, opts.visibleMount);
  }
  if (needsQuickHiddenCapture()) {
    return extractWithQuickHiddenSession(url, maxEdge, pathLabel);
  }
  return extractWithOffscreen(url, maxEdge, pathLabel);
}

/** Firefox/mobile: canvas capture always returns black — use ffmpeg.wasm directly. */
export function prefersFfmpegThumbCapture(): boolean {
  return needsQuickHiddenCapture();
}

async function extractWithFfmpegOverlay(
  file: File,
  pathLabel: string,
  seekSec: number,
  maxEdge: number
): Promise<Blob> {
  const session = beginThumbCaptureSession();
  try {
    const { createVideoThumbnailFromFileFfmpeg } = await import('./videoThumbnailFfmpeg');
    return await createVideoThumbnailFromFileFfmpeg(file, pathLabel, seekSec, maxEdge);
  } finally {
    endThumbCaptureSession(session);
    endAllThumbCaptureSessions();
  }
}

/** Extract a JPEG frame from a local video file (attach/save — never downloads from GitHub). */
export async function createVideoThumbnailFromFile(
  file: File,
  seekSec = 0.5,
  maxEdge = 400,
  debugRepoPath?: string,
  opts?: VideoThumbnailCaptureOptions
): Promise<Blob> {
  const pathLabel = debugRepoPath ?? `pending:${file.name}`;

  if (prefersFfmpegThumbCapture()) {
    logThumbDebug(pathLabel, 'thumb-ingest-ffmpeg', 'Skipping canvas capture', true);
    return extractWithFfmpegOverlay(file, pathLabel, seekSec, maxEdge);
  }

  const url = URL.createObjectURL(file);
  try {
    return await createVideoThumbnailFromUrl(url, maxEdge, pathLabel, opts);
  } finally {
    URL.revokeObjectURL(url);
  }
}
